import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchMatches } from "../lib/api";
import { Button } from "./ui/button";
import { ArrowRight } from "lucide-react";
import { MonthGroup } from "../pages/Archive";

const monthName = (m) =>
  ["January","February","March","April","May","June",
   "July","August","September","October","November","December"][m];

const groupByMonth = (list) => {
  const groups = {};
  for (const m of list) {
    if (!m.date) continue;
    const [y, mo] = m.date.split("-");
    const key = `${y}-${mo}`;
    const label = `${monthName(parseInt(mo, 10) - 1)} ${y}`;
    if (!groups[key]) groups[key] = { key, label, y, mo, items: [] };
    groups[key].items.push(m);
  }
  return Object.values(groups).sort((a, b) => (a.key < b.key ? 1 : -1));
};

const PreviousTips = ({ limit = 30 }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);

  const load = async () => {
    try {
      const list = await fetchMatches();
      setRows(list.slice(0, limit));
    } catch {}
  };

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("ep-data-updated", handler);
    const iv = setInterval(load, 20000);
    return () => {
      window.removeEventListener("ep-data-updated", handler);
      clearInterval(iv);
    };
  }, [limit]);

  const groups = useMemo(() => groupByMonth(rows), [rows]);

  return (
    <section className="bg-[#f5f7ff] py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-[#ff5a5a] font-semibold tracking-widest text-xs uppercase">
              Previous Tips
            </div>
            <h2 className="text-[#0a1a4a] text-3xl sm:text-4xl font-bold mt-3">
              Recent {limit} picks — all results
            </h2>
            <p className="mt-3 text-[#0a1a4a]/70 max-w-xl">
              A live look at our most recent {limit} published selections.
              Every result — win, loss, or draw — stays visible.
            </p>
          </div>
          <Button
            onClick={() => navigate("/archive")}
            variant="outline"
            className="bg-white hover:bg-slate-50 border-slate-200 text-[#0a1a4a] font-semibold h-11 px-5"
          >
            View full archive
            <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>

        {groups.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl py-16 text-center text-slate-500">
            Loading recent picks...
          </div>
        ) : (
          <div className="space-y-10">
            {groups.map((g) => (
              <MonthGroup key={g.key} group={g} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default PreviousTips;
